import type { Task } from '../../lib/tasksApi';
import type { Reminder } from '../../lib/remindersApi';
import type { MonthGridDay } from '../../lib/calendarDates';
import { toDateKey } from '../../lib/calendarDates';
import { formatEstimate } from '../../lib/estimate';
import { dayWorkTotals } from '../../lib/calendarTotals';
import { CalendarDnd, DraggableCalendarItem, DroppableCalendarDay } from './calendarDnd';
import { useMoveTaskToDay } from './useMoveTaskToDay';
import { sortDayTasks } from '../../lib/calendarTaskOrder';
import { CalendarEventChip, LeaveChip, MeetingChip, ReminderChip, TaskChip } from './chips';
import type { VideoMeetingSummary } from '../../lib/meetApi';
import type { CalendarEvent } from '../../lib/calendarApi';
import type { LeaveDay } from '../../lib/leaveApi';
import { TaskListRow, calendarTaskRow } from '../tasks/TaskListRow';
import { useSidebar } from '../layout/SidebarContext';
import { useI18n } from '../../contexts/I18nContext';

interface WeekGridProps {
  /** The 7 Monday-first days of the active week (from weekGrid()). */
  days: MonthGridDay[];
  /** Tasks bucketed by local YYYY-MM-DD due-date key. */
  tasksByDay: Map<string, Task[]>;
  /** The caller's firing reminders (the chips) bucketed by local YYYY-MM-DD due key. */
  remindersByDay?: Map<string, Reminder[]>;
  /**
   * Every open reminder of the day, for "Total estimat" — a reminder is time on
   * the plate before its chip appears. Defaults to `remindersByDay`.
   */
  plannedRemindersByDay?: Map<string, Reminder[]>;
  /** Firmio Meet meetings bucketed by the local YYYY-MM-DD of their start. */
  meetingsByDay?: Map<string, VideoMeetingSummary[]>;
  /** The selected person's Google Calendar events, bucketed by local day. */
  eventsByDay?: Map<string, CalendarEvent[]>;
  /**
   * Approved leave, already one entry per covered day (the server fans a
   * request out), so a day's bucket is simply everyone away on it.
   */
  leaveByDay?: Map<string, LeaveDay[]>;
  /** Label each chip with its assignee — on when no assignee filter narrows the view. */
  showAssignee?: boolean;
  /** The clock "Total rămas" is measured against (server-corrected); now when omitted. */
  nowMs?: number;
}

/** The day-number pill, filled when the day is today. */
function DayNumber({ date, isToday }: { date: Date; isToday: boolean }) {
  return (
    <span
      className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs ${
        isToday ? 'bg-brand-600 font-semibold text-white' : 'text-gray-700'
      }`}
    >
      {date.getDate()}
    </span>
  );
}

/**
 * The weekly calendar view: 7 day columns (Monday-first) each listing that
 * day's tasks. When any task on a day carries an estimate, the column header
 * shows the summed estimated time for the day (CLE-446).
 *
 * On a phone the seven columns are ~50px wide, which truncates every chip to
 * three characters, so the mobile layout is a vertical agenda instead — same
 * days, but the tasks as the shared task row every other list uses.
 */
export function WeekGrid(props: WeekGridProps) {
  const { isMobile } = useSidebar();
  return isMobile ? <WeekAgenda {...props} /> : <WeekColumns {...props} />;
}

/**
 * Phone layout: one card per day, stacked. Days with nothing on them collapse
 * to a single header row so the week's shape stays readable without seven
 * empty boxes taking over the screen.
 */
function WeekAgenda({
  days,
  tasksByDay,
  remindersByDay,
  plannedRemindersByDay,
  meetingsByDay,
  eventsByDay,
  leaveByDay,
  showAssignee,
  nowMs,
}: WeekGridProps) {
  const { t, locale } = useI18n();
  const todayKey = toDateKey(new Date());
  const weekdayFmt = new Intl.DateTimeFormat(locale, { weekday: 'long' });
  const monthFmt = new Intl.DateTimeFormat(locale, { month: 'short' });

  return (
    <div className="space-y-2">
      {days.map((day) => {
        const tasks = sortDayTasks(tasksByDay.get(day.key) ?? []);
        const reminders = remindersByDay?.get(day.key) ?? [];
        const meetings = meetingsByDay?.get(day.key) ?? [];
        const events = eventsByDay?.get(day.key) ?? [];
        const leave = leaveByDay?.get(day.key) ?? [];
        // Someone being away is something to see on the day, so a day that
        // carries only a leave band is not an empty day.
        const isEmpty =
          tasks.length === 0 &&
          reminders.length === 0 &&
          meetings.length === 0 &&
          events.length === 0 &&
          leave.length === 0;
        const totals = dayWorkTotals(
          tasks,
          plannedRemindersByDay?.get(day.key) ?? reminders,
          events,
          meetings,
          nowMs,
        );
        return (
          <section
            key={day.key}
            className="overflow-hidden rounded-lg border border-gray-200 bg-white"
          >
            <div
              className={`flex items-center justify-between gap-2 px-2 py-1.5 ${
                isEmpty ? '' : 'border-b border-gray-200 bg-gray-50'
              }`}
            >
              <div className="flex min-w-0 items-center gap-2">
                <DayNumber date={day.date} isToday={day.key === todayKey} />
                <span className="truncate text-sm font-medium capitalize text-gray-700">
                  {weekdayFmt.format(day.date)}
                </span>
                <span className="shrink-0 text-xs text-gray-400">{monthFmt.format(day.date)}</span>
              </div>
              {isEmpty ? (
                <span className="shrink-0 text-xs text-gray-400">{t('calendar.weekEmpty')}</span>
              ) : (
                totals != null && (
                  <span className="flex shrink-0 flex-col items-end text-xs font-medium text-gray-500">
                    <span>
                      {t('calendar.estimatedTotal')}: {formatEstimate(totals.estimated)}
                    </span>
                    <span>
                      {t('calendar.remainingTotal')}: {formatEstimate(totals.remaining)}
                    </span>
                  </span>
                )
              )}
            </div>
            {!isEmpty && (
              <div className="py-1">
                {/* Leave leads: being away is the frame the rest of the day sits
                    in, not another item in it. Then reminders (Andrei,
                    2026-09-30), meetings and calendar events — agenda-style —
                    and finally the day's tasks. */}
                {(leave.length > 0 ||
                  reminders.length > 0 ||
                  meetings.length > 0 ||
                  events.length > 0) && (
                  <div className="space-y-1 px-2 pb-1 pt-1">
                    {leave.map((entry) => (
                      <LeaveChip
                        key={entry.requestId}
                        leave={entry}
                        locale={locale}
                        showAssignee={showAssignee}
                      />
                    ))}
                    {reminders.map((reminder) => (
                      <ReminderChip key={reminder.id} reminder={reminder} locale={locale} />
                    ))}
                    {meetings.map((meeting) => (
                      <MeetingChip key={meeting.id} meeting={meeting} locale={locale} />
                    ))}
                    {events.map((event) => (
                      <CalendarEventChip key={event.id} event={event} locale={locale} />
                    ))}
                  </div>
                )}
                {/* The same row as the summaries and the tasks list; the
                    avatar only when the view is not already filtered to one
                    person. */}
                <ul>
                  {tasks.map((task) => (
                    <TaskListRow
                      key={task.id}
                      task={calendarTaskRow(task)}
                      hideAssignee={!showAssignee}
                    />
                  ))}
                </ul>
              </div>
            )}
          </section>
        );
      })}
    </div>
  );
}

/** Desktop layout: the seven-column grid. */
function WeekColumns({
  days,
  tasksByDay,
  remindersByDay,
  plannedRemindersByDay,
  meetingsByDay,
  eventsByDay,
  leaveByDay,
  showAssignee,
  nowMs,
}: WeekGridProps) {
  const { t, locale } = useI18n();
  const todayKey = toDateKey(new Date());
  const weekdayFmt = new Intl.DateTimeFormat(locale, { weekday: 'short' });
  const moveTask = useMoveTaskToDay();

  return (
    <CalendarDnd<Task>
      onMove={moveTask}
      renderOverlay={(task) => <TaskChip task={task} showAssignee={showAssignee} />}
    >
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white">
        <div className="grid grid-cols-7">
          {days.map((day) => {
            const tasks = sortDayTasks(tasksByDay.get(day.key) ?? []);
            const reminders = remindersByDay?.get(day.key) ?? [];
            const meetings = meetingsByDay?.get(day.key) ?? [];
            const events = eventsByDay?.get(day.key) ?? [];
            const leave = leaveByDay?.get(day.key) ?? [];
            // Leave is absence, not work, so it stays out of the day's totals.
            const totals = dayWorkTotals(
              tasks,
              plannedRemindersByDay?.get(day.key) ?? reminders,
              events,
              meetings,
              nowMs,
            );
            return (
              <DroppableCalendarDay
                key={day.key}
                dayKey={day.key}
                className="min-h-64 border-r border-gray-100 last:border-r-0"
              >
                <div className="border-b border-gray-200 bg-gray-50 px-2 py-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-medium capitalize text-gray-500">
                      {weekdayFmt.format(day.date)}
                    </span>
                    <DayNumber date={day.date} isToday={day.key === todayKey} />
                  </div>
                  {totals != null && (
                    <div className="mt-1 space-y-0.5 text-[11px] font-medium text-gray-500">
                      <div className="truncate" title={t('calendar.estimatedTotal')}>
                        {t('calendar.estimatedTotal')}: {formatEstimate(totals.estimated)}
                      </div>
                      <div className="truncate" title={t('calendar.remainingTotal')}>
                        {t('calendar.remainingTotal')}: {formatEstimate(totals.remaining)}
                      </div>
                    </div>
                  )}
                </div>
                <div className="space-y-1 p-1.5">
                  {/* Leave leads: being away is the frame the rest of the day sits
                    in, not another item in it. Then reminders (Andrei,
                    2026-09-30), meetings and calendar events — agenda-style —
                    and finally the day's tasks. */}
                  {leave.map((entry) => (
                    <LeaveChip
                      key={entry.requestId}
                      leave={entry}
                      locale={locale}
                      showAssignee={showAssignee}
                    />
                  ))}
                  {reminders.map((reminder) => (
                    <ReminderChip key={reminder.id} reminder={reminder} locale={locale} />
                  ))}
                  {meetings.map((meeting) => (
                    <MeetingChip key={meeting.id} meeting={meeting} locale={locale} />
                  ))}
                  {events.map((event) => (
                    <CalendarEventChip key={event.id} event={event} locale={locale} />
                  ))}
                  {tasks.length === 0 &&
                  reminders.length === 0 &&
                  meetings.length === 0 &&
                  events.length === 0 &&
                  leave.length === 0 ? (
                    <p className="px-1 py-0.5 text-xs font-medium text-gray-500">
                      {t('calendar.weekEmpty')}
                    </p>
                  ) : (
                    tasks.map((task) => (
                      <DraggableCalendarItem
                        key={task.id}
                        id={task.id}
                        item={task}
                        dayKey={day.key}
                      >
                        <TaskChip task={task} showAssignee={showAssignee} />
                      </DraggableCalendarItem>
                    ))
                  )}
                </div>
              </DroppableCalendarDay>
            );
          })}
        </div>
      </div>
    </CalendarDnd>
  );
}
